(() => {
  if (customElements.get('carousel-loading-bar')) return;

class CarouselLoadingBar extends HTMLElement {
  static DURATION_MS = 3000;
  static MOBILE_HOME_PRODUCT_DURATION_MS = 6000;
  static SLIDE_MS = 420;
  static MOBILE_QUERY = '(max-width: 749px)';
  static DESKTOP_QUERY = '(min-width: 750px)';
  static SWIPE_MIN_DISTANCE = 42;
  static SWIPE_AXIS_RATIO = 1.25;
  static SWIPE_TOUCH_AXIS_RATIO = 1;
  static SWIPE_START_DISTANCE = 8;
  static SWIPE_COMMIT_FRACTION = 0.18;
  static SWIPE_COMMIT_MIN_DISTANCE = 20;
  static SWIPE_FLICK_VELOCITY = 0.4; // px per ms
  static SWIPE_FLICK_MIN_DISTANCE = 12;

  constructor() {
    super();
    this.activeIndex = 0;
    this.isPlaying = false;
    this.mode = 'panel';
    this.thumbs = [];
    this.slides = [];
    this.cardFinishActiveDrag = null;
    this.onGallerySlideChanged = this.onGallerySlideChanged.bind(this);
    this.onGalleryUserNavigated = this.onGalleryUserNavigated.bind(this);
    this.onSlideshowSlideChanged = this.onSlideshowSlideChanged.bind(this);
    this.onSlideshowAutoplayChanged = this.onSlideshowAutoplayChanged.bind(this);
    this.onProgressEnd = this.onProgressEnd.bind(this);
    this.onHoverEnter = this.onHoverEnter.bind(this);
    this.onHoverLeave = this.onHoverLeave.bind(this);
    this.bindGallery = this.bindGallery.bind(this);
    this.onSlideshowPointerDown = this.onSlideshowPointerDown.bind(this);
    this.onSlideshowPointerMove = this.onSlideshowPointerMove.bind(this);
    this.onSlideshowPointerUp = this.onSlideshowPointerUp.bind(this);
    this.onSlideshowPointerCancel = this.onSlideshowPointerCancel.bind(this);
    this.onSlideshowClickCapture = this.onSlideshowClickCapture.bind(this);
    this.onSlideshowDragStart = this.onSlideshowDragStart.bind(this);
    this.onSlideshowNativeScroll = this.onSlideshowNativeScroll.bind(this);
    this.onSlideshowSettle = this.onSlideshowSettle.bind(this);
    this.onCardPointerDown = this.onCardPointerDown.bind(this);
    this.onCardPointerMove = this.onCardPointerMove.bind(this);
    this.onCardPointerUp = this.onCardPointerUp.bind(this);
    this.onCardPointerCancel = this.onCardPointerCancel.bind(this);
    this.onCardClickCapture = this.onCardClickCapture.bind(this);
    this.onCardDragStart = this.onCardDragStart.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
  }

  connectedCallback() {
    this.slideshow = this.closest('[data-gigi-slideshow]');
    this.gallery = this.closest('[data-gfs-gallery], [data-gigi-card-gallery]');
    this.dots = Array.from(this.querySelectorAll('[data-carousel-loading-bar-dot]'));
    this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;

    if (this.slideshow) {
      this.mode = 'slideshow';
      this.gallery = this.slideshow;
    } else if (this.gallery) {
      this.mode = this.gallery.hasAttribute('data-gigi-card-gallery') ? 'card' : 'panel';
    } else {
      this.hidden = true;
      return;
    }

    if (this.dots.length < 2) {
      this.hidden = true;
      return;
    }

    if (this.mode === 'slideshow') {
      this.hoverTarget = this.slideshow;
      this.slideshow.addEventListener('slideChanged', this.onSlideshowSlideChanged);
      this.slideshow.addEventListener('gigiSlideshowAutoplayChanged', this.onSlideshowAutoplayChanged);
    } else {
      this.hoverTarget =
        (this.mode === 'card' && this.gallery) ||
        this.closest('[data-gigi-gallery-hover], [data-gfs-gallery-hover]') ||
        this.gallery.querySelector('.gfs-panel__img-wrapper, .gigi-card-media-gallery__hover') ||
        this.gallery;
    }

    this.addEventListener('click', this.onDotClick);
    if (this.reducedMotion?.addEventListener) {
      this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
    } else {
      this.reducedMotion?.addListener?.(this.onReducedMotionChange);
    }

    if (this.mode === 'panel') {
      this.gallery.addEventListener('gigiGallerySlideChanged', this.onGallerySlideChanged);
      this.gallery.addEventListener('gigiGalleryUserNavigated', this.onGalleryUserNavigated);
    }

    if (this.mode !== 'slideshow') {
      this.hoverTarget.addEventListener('pointerenter', this.onHoverEnter);
      this.hoverTarget.addEventListener('pointerleave', this.onHoverLeave);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.bindGallery, { once: true });
    } else {
      this.bindGalleryFrame = requestAnimationFrame(this.bindGallery);
    }
  }

  bindGallery() {
    this.bindGalleryFrame = null;
    if (!this.isConnected || !this.gallery) return;

    if (this.mode === 'slideshow') {
      this.slider = this.slideshow.querySelector('[id^="Slider-"]');
      this.slides = Array.from(
        this.slider?.querySelectorAll('.slideshow__slide, [id^="Slide-"]') || []
      ).filter((slide) => !slide.classList.contains('gigi-slideshow-edge-clone'));
      this.setupSlideshowLoop();
      this.syncFromSlideshow();
      this.syncSlideshowAutoplay();
      this.bindSlideshowSwipe();
      return;
    }

    if (this.mode === 'panel') {
      this.thumbs = Array.from(this.gallery.querySelectorAll('[data-gfs-gallery-thumb]'));
      this.syncFromGallery();
      return;
    }

    this.slides = Array.from(this.gallery.querySelectorAll('[data-gigi-gallery-image]'));
    this.gallery.dataset.gigiActiveIndex = String(
      Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      )
    );
    this.syncFromSlides();
    this.bindCardSwipe();
  }

  disconnectedCallback() {
    document.removeEventListener('DOMContentLoaded', this.bindGallery);
    if (this.bindGalleryFrame != null) {
      cancelAnimationFrame(this.bindGalleryFrame);
      this.bindGalleryFrame = null;
    }
    this.removeEventListener('click', this.onDotClick);
    if (this.reducedMotion?.removeEventListener) {
      this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
    } else {
      this.reducedMotion?.removeListener?.(this.onReducedMotionChange);
    }
    this.gallery?.removeEventListener('gigiGallerySlideChanged', this.onGallerySlideChanged);
    this.gallery?.removeEventListener('gigiGalleryUserNavigated', this.onGalleryUserNavigated);
    this.slideshow?.removeEventListener('slideChanged', this.onSlideshowSlideChanged);
    this.slideshow?.removeEventListener('gigiSlideshowAutoplayChanged', this.onSlideshowAutoplayChanged);
    this.hoverTarget?.removeEventListener('pointerenter', this.onHoverEnter);
    this.hoverTarget?.removeEventListener('pointerleave', this.onHoverLeave);
    this.unbindSlideshowSwipe();
    this.unbindCardSwipe();
    this.teardownSlideshowLoop();
    this.clearProgressAnimation();
  }

  onDotClick(event) {
    if (!(event.target instanceof Element)) return;

    const dot = event.target.closest('[data-carousel-loading-bar-dot]');
    if (!dot || !this.contains(dot)) return;

    const index = this.dots.indexOf(dot);
    if (index < 0) return;

    event.preventDefault();
    if (this.mode === 'panel') {
      this.thumbs[index]?.click();
      return;
    }

    if (this.mode === 'slideshow') {
      if (index !== this.activeIndex) this.goToSlideshowSlide(index);
      return;
    }

    this.goToSlide(index);
  }

  onReducedMotionChange(event) {
    if (event.matches) {
      this.clearProgressAnimation();
      return;
    }

    if (this.mode !== 'slideshow' && this.hoverTarget?.matches?.(':hover')) {
      this.startHover();
    }
  }

  onSlideshowSlideChanged(event) {
    // In loop mode the native-scroll handler owns the active index; Dawn's
    // slideChanged is skewed one slide high by the leading clone, so ignore it.
    if (this.slideshowLoopEnabled) return;

    if (
      this.slideshow?.dataset.gigiSlideshowSliding === 'true' ||
      this.slideshow?.dataset.gigiSlideshowDragging === 'true'
    ) {
      return;
    }

    const currentPage = event.detail?.currentPage;
    if (typeof currentPage !== 'number' || currentPage < 1) return;

    const nextIndex = currentPage - 1;
    if (nextIndex === this.activeIndex) return;

    this.setActiveIndex(nextIndex, this.isPlaying);
  }

  onSlideshowAutoplayChanged(event) {
    if (this.mode !== 'slideshow') return;

    if (event.detail?.playing) {
      this.startSlideshowProgress();
    } else {
      this.clearProgressAnimation();
    }
  }

  syncSlideshowAutoplay() {
    if (this.mode !== 'slideshow' || !this.slider) return;

    const isAutoplaying =
      this.slider.dataset.autoplay === 'true' && this.slider.getAttribute('aria-live') === 'off';

    if (isAutoplaying) {
      this.startSlideshowProgress();
    } else {
      this.clearProgressAnimation();
    }
  }

  startSlideshowProgress() {
    if (this.mode !== 'slideshow' || this.reducedMotion?.matches || this.getSlideCount() < 2) return;

    this.isPlaying = true;
    this.classList.add('is-playing');
    this.classList.remove('is-paused');
    this.playProgress();
  }

  syncFromSlideshow() {
    if (!this.slider || !this.slides.length) return;
    if (
      this.slideshow.dataset.gigiSlideshowSliding === 'true' ||
      this.slideshow.dataset.gigiSlideshowDragging === 'true'
    ) {
      return;
    }

    const activeIndex = Math.max(
      0,
      this.slides.findIndex((slide) => {
        const slideLeft = slide.offsetLeft;
        const slideRight = slideLeft + slide.clientWidth;
        const viewportLeft = this.slider.scrollLeft;
        const viewportRight = viewportLeft + this.slider.clientWidth;
        return slideLeft >= viewportLeft - 1 && slideRight <= viewportRight + 1;
      })
    );

    this.setActiveIndex(activeIndex, false);
  }

  // --- Infinite (wrap-around) swipe ------------------------------------------
  // Adds a clone of the last slide before the first, and a clone of the first
  // after the last, so the native scroll track has runway on both ends. When
  // scrolling settles on a clone we teleport (instantly, behavior:auto) to the
  // matching real slide; because each clone is pixel-identical to its target,
  // the jump is invisible. This makes touch and trackpad swipes wrap in both
  // directions while keeping native momentum scrolling.
  setupSlideshowLoop() {
    if (this.slideshowLoopEnabled || !this.slider) return;
    if (this.slides.length < 2) return;
    // The theme editor re-renders sections on edit; skip cloning there so block
    // selection keeps working. Falls back to the non-wrapping default in-editor.
    if (window.Shopify && window.Shopify.designMode) return;

    this.realSlides = this.slides;
    const count = this.realSlides.length;

    this.headClone = this.buildEdgeClone(this.realSlides[count - 1]);
    this.tailClone = this.buildEdgeClone(this.realSlides[0]);
    if (!this.headClone || !this.tailClone) {
      this.headClone?.remove();
      this.tailClone?.remove();
      this.headClone = null;
      this.tailClone = null;
      return;
    }

    this.slider.insertBefore(this.headClone, this.slider.firstChild);
    this.slider.append(this.tailClone);

    this.slideshowLoopEnabled = true;
    this.slideshowLoopWidth = this.slider.clientWidth;
    this.recenterSlideshowLoop(this.activeIndex);
    this.applyLoopVisibility(this.activeIndex);

    this.slider.addEventListener('scroll', this.onSlideshowNativeScroll, { passive: true });
    this.slideshowResizeObserver = new ResizeObserver(() => this.onSlideshowLoopResize());
    this.slideshowResizeObserver.observe(this.slider);
  }

  teardownSlideshowLoop() {
    if (!this.slideshowLoopEnabled) return;

    this.slideshowLoopEnabled = false;
    window.clearTimeout(this.slideshowSettleTimer);
    this.slider?.removeEventListener('scroll', this.onSlideshowNativeScroll);
    this.slideshowResizeObserver?.disconnect();
    this.slideshowResizeObserver = null;
    this.headClone?.remove();
    this.tailClone?.remove();
    this.headClone = null;
    this.tailClone = null;
    this.slideshowClonesPrimed = false;
  }

  buildEdgeClone(sourceSlide) {
    if (!sourceSlide) return null;

    const clone = sourceSlide.cloneNode(true);
    clone.classList.add('gigi-slideshow-edge-clone');
    clone.classList.remove('gigi-slideshow-loop-clone');
    clone.removeAttribute('id');
    clone.removeAttribute('role');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    clone.style.pointerEvents = 'none';

    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('a, button, input, select, textarea, summary, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    clone.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.fetchPriority = 'low';
    });

    return clone;
  }

  // Clone images start lazy and low-priority so they don't compete with the hero
  // image. Prime them only after the visitor starts interacting with the carousel.
  primeSlideshowClones() {
    if (this.slideshowClonesPrimed) return;
    this.slideshowClonesPrimed = true;
    [this.headClone, this.tailClone].forEach((clone) => {
      clone?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = 'auto';
      });
    });
  }

  getSlideshowStep() {
    if (!this.realSlides || !this.realSlides.length) return this.slider?.clientWidth || 0;
    if (this.realSlides.length < 2) {
      return this.realSlides[0].clientWidth || this.slider?.clientWidth || 0;
    }
    return (
      Math.abs(this.realSlides[1].offsetLeft - this.realSlides[0].offsetLeft) ||
      this.realSlides[0].clientWidth ||
      this.slider?.clientWidth ||
      0
    );
  }

  // Maps the current scroll position to a real-slide index, treating a rest on
  // the head clone as the last slide and the tail clone as the first.
  getSlideshowReprIndex() {
    const step = this.getSlideshowStep();
    if (!step || !this.realSlides) return null;

    const count = this.realSlides.length;
    let slot = Math.round(this.slider.scrollLeft / step);
    slot = Math.max(0, Math.min(count + 1, slot));

    if (slot === 0) return count - 1;
    if (slot === count + 1) return 0;
    return slot - 1;
  }

  recenterSlideshowLoop(realIndex) {
    if (!this.slideshowLoopEnabled || !this.slider) return;
    const target = this.realSlides[realIndex];
    if (!target) return;

    this.slideshowTeleporting = true;
    const previousScrollBehavior = this.slider.style.scrollBehavior;
    this.slider.style.scrollBehavior = 'auto';
    this.slider.scrollLeft = target.offsetLeft;
    this.slider.style.scrollBehavior = previousScrollBehavior;
    requestAnimationFrame(() => {
      this.slideshowTeleporting = false;
    });
  }

  // Mirrors Dawn's setSlideVisibility but with the loop-correct index, since the
  // leading clone shifts offsets and makes Dawn's own counter run one slide high.
  applyLoopVisibility(realIndex) {
    if (!this.realSlides) return;

    this.realSlides.forEach((slide, index) => {
      const isActive = index === realIndex;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        slide.removeAttribute('tabindex');
      } else {
        slide.setAttribute('tabindex', '-1');
      }
      slide.querySelectorAll('a').forEach((link) => {
        if (isActive) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  onSlideshowNativeScroll() {
    if (!this.slideshowLoopEnabled) return;
    if (
      this.slideshow.dataset.gigiSlideshowSliding === 'true' ||
      this.slideshow.dataset.gigiSlideshowDragging === 'true'
    ) {
      return;
    }

    const repr = this.getSlideshowReprIndex();
    if (repr == null) return;

    if (repr !== this.activeIndex) this.setActiveIndex(repr, this.isPlaying);
    // Must run even mid-teleport: the teleport fires a scroll event on which
    // Dawn's clone-skewed setSlideVisibility marks the wrong slide visible, and
    // this listener (registered last) is what overrides it back to the truth.
    this.applyLoopVisibility(repr);

    // The teleport is programmatic and lands on a real slide: don't prime images
    // (that would defeat the LCP deferral on the initial recenter), and don't
    // schedule/re-enter a settle pass. Only genuine user scrolling does both.
    if (this.slideshowTeleporting) return;

    this.primeSlideshowClones();
    this.scheduleSlideshowSettle();
  }

  scheduleSlideshowSettle() {
    window.clearTimeout(this.slideshowSettleTimer);
    this.slideshowSettleTimer = window.setTimeout(this.onSlideshowSettle, 90);
  }

  onSlideshowSettle() {
    if (!this.slideshowLoopEnabled || this.slideshowTeleporting) return;
    if (
      this.slideshow.dataset.gigiSlideshowSliding === 'true' ||
      this.slideshow.dataset.gigiSlideshowDragging === 'true'
    ) {
      return;
    }

    const step = this.getSlideshowStep();
    if (!step) return;

    const count = this.realSlides.length;
    const scrollLeft = this.slider.scrollLeft;
    const headLeft = this.headClone ? this.headClone.offsetLeft : 0;
    const tailLeft = this.tailClone ? this.tailClone.offsetLeft : (count + 1) * step;
    // Only teleport once scrolling has truly come to rest on a clone — checking a
    // tolerance (not just the rounded slot) avoids a visible yank mid-glide.
    const tolerance = Math.max(2, step * 0.02);

    if (Math.abs(scrollLeft - headLeft) <= tolerance) {
      this.recenterSlideshowLoop(count - 1);
      this.setActiveIndex(count - 1, false);
      this.applyLoopVisibility(count - 1);
    } else if (Math.abs(scrollLeft - tailLeft) <= tolerance) {
      this.recenterSlideshowLoop(0);
      this.setActiveIndex(0, false);
      this.applyLoopVisibility(0);
    } else {
      const slot = Math.round(scrollLeft / step);
      if (slot <= 0 || slot >= count + 1) this.scheduleSlideshowSettle();
    }
  }

  onSlideshowLoopResize() {
    if (!this.slideshowLoopEnabled) return;
    if (
      this.slideshow.dataset.gigiSlideshowSliding === 'true' ||
      this.slideshow.dataset.gigiSlideshowDragging === 'true'
    ) {
      return;
    }

    const width = this.slider.clientWidth;
    if (width === this.slideshowLoopWidth) return;
    this.slideshowLoopWidth = width;
    this.recenterSlideshowLoop(this.activeIndex);
  }

  // Seamless last<->first slide using the persistent edge clones: scroll to the
  // adjacent clone (continuing in the same visual direction), then teleport to
  // the matching real slide.
  async runSlideshowLoopWrap(index, direction, restartProgress) {
    const edgeClone = direction === 1 ? this.tailClone : this.headClone;

    if (!edgeClone) {
      this.recenterSlideshowLoop(index);
      this.setActiveIndex(index, restartProgress);
      this.applyLoopVisibility(index);
      this.dispatchSlideshowSlideChanged(index);
      return;
    }

    this.preloadSlideshowSlide(index);
    this.slideshow.dataset.gigiSlideshowSliding = 'true';

    try {
      const edgeLeft = edgeClone.offsetLeft;
      this.slider.scrollTo({
        left: edgeLeft,
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
      });
      await this.waitForSlideshowScroll(edgeLeft);
      this.recenterSlideshowLoop(index);
    } finally {
      delete this.slideshow.dataset.gigiSlideshowSliding;
    }

    this.setActiveIndex(index, restartProgress);
    this.applyLoopVisibility(index);
    this.dispatchSlideshowSlideChanged(index);
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }

  isMobileHomeProductGallery() {
    if (this.mode === 'slideshow') return false;

    return (
      window.matchMedia?.(CarouselLoadingBar.MOBILE_QUERY)?.matches === true &&
      Boolean(this.closest('#shop-gigi'))
    );
  }

  dispatchSlideshowSlideChanged(index) {
    this.slideshow.dispatchEvent(
      new CustomEvent('slideChanged', {
        detail: {
          currentPage: index + 1,
          currentElement: this.slides[index],
        },
      })
    );
  }

  resetSlideshowSlideStyles() {
    this.slider?.querySelector('.gigi-slideshow-wrap-stage')?.remove();
    this.slider?.querySelector('.gigi-slideshow-loop-clone')?.remove();

    this.slides.forEach((slide) => {
      slide.style.transform = '';
      slide.style.transition = '';
      slide.style.visibility = '';
      slide.style.position = '';
      slide.style.top = '';
      slide.style.left = '';
      slide.style.width = '';
      slide.style.height = '';
      slide.style.zIndex = '';
      slide.style.pointerEvents = '';
    });

    this.slider?.classList.remove('is-gigi-sliding-active');
    if (this.slider) {
      this.slider.style.minHeight = '';
    }
  }

  finishSlideshowNavigation(index) {
    this.setActiveIndex(index, this.isPlaying);
    if (this.slideshowLoopEnabled) this.applyLoopVisibility(index);
    this.dispatchSlideshowSlideChanged(index);
    if (this.isPlaying) this.slideshow?.play?.();
  }

  setSlideshowScrollPosition(index) {
    if (!this.slider || !this.slides[index]) return;

    const previousScrollBehavior = this.slider.style.scrollBehavior;
    this.slider.style.scrollBehavior = 'auto';
    this.slider.scrollLeft = this.slides[index].offsetLeft;
    this.slider.style.scrollBehavior = previousScrollBehavior;
  }

  createSlideshowLoopClone(index) {
    const sourceSlide = this.slides[index];
    if (!this.slider || !sourceSlide) return null;

    this.slider.querySelector('.gigi-slideshow-loop-clone')?.remove();

    const clone = sourceSlide.cloneNode(true);
    clone.classList.add('gigi-slideshow-loop-clone');
    clone.removeAttribute('id');
    clone.removeAttribute('role');
    clone.removeAttribute('tabindex');
    clone.setAttribute('aria-hidden', 'true');
    clone.style.pointerEvents = 'none';

    clone.querySelectorAll('[id]').forEach((element) => {
      element.removeAttribute('id');
    });

    clone.querySelectorAll('a, button, input, select, textarea, summary, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    clone.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });

    this.slider.append(clone);
    return clone;
  }

  waitForSlideshowScroll(targetLeft) {
    return new Promise((resolve) => {
      const startedAt = performance.now();

      const checkPosition = () => {
        const isAtTarget = Math.abs(this.slider.scrollLeft - targetLeft) <= 1;
        const timedOut = performance.now() - startedAt > CarouselLoadingBar.SLIDE_MS + 240;

        if (isAtTarget || timedOut) {
          resolve();
          return;
        }

        requestAnimationFrame(checkPosition);
      };

      requestAnimationFrame(checkPosition);
    });
  }

  goToSlideshowSlide(index, { allowLoopWrap = false } = {}) {
    if (!this.slider || !this.slides[index]) return;
    if (this.slideshow.dataset.gigiSlideshowSliding === 'true') return;

    const count = this.getSlideCount();
    const prevIndex = this.activeIndex;
    if (prevIndex === index) return;

    const direction = this.getSlideDirection(prevIndex, index, count);
    if (direction === 0) return;

    const restartProgress = this.isPlaying;
    if (restartProgress) {
      this.clearProgressAnimation(false);
    }

    this.preloadSlideshowSlide(index + 1);

    // Sequential advances (autoplay) wrap last->first via the edge clone so the
    // slide keeps moving in the same direction. Dot clicks keep their direct path.
    if (this.slideshowLoopEnabled && allowLoopWrap) {
      const isForwardWrap = prevIndex === count - 1 && index === 0;
      const isBackwardWrap = prevIndex === 0 && index === count - 1;
      if (isForwardWrap || isBackwardWrap) {
        this.runSlideshowLoopWrap(index, isForwardWrap ? 1 : -1, restartProgress);
        return;
      }
    }

    if (this.prefersReducedMotion()) {
      this.resetSlideshowSlideStyles();
      this.setSlideshowScrollPosition(index);
      this.finishSlideshowNavigation(index);
      return;
    }

    this.runSlideshowSlideAnimation(index, prevIndex, direction, count);
  }

  async waitForSlideImages(slide) {
    const images = Array.from(slide.querySelectorAll('img'));

    await Promise.all(
      images.map((image) => {
        image.loading = 'eager';

        if (image.complete && image.naturalWidth > 0) {
          return image.decode?.().catch(() => undefined) || Promise.resolve();
        }

        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        }).then(() => image.decode?.().catch(() => undefined));
      })
    );
  }

  async runSlideshowSlideAnimation(index, prevIndex, direction, count) {
    const incomingSlide = this.slides[index];

    if (!incomingSlide) {
      this.resetSlideshowSlideStyles();
      this.setSlideshowScrollPosition(index);
      this.finishSlideshowNavigation(index);
      return;
    }

    this.slideshow.dataset.gigiSlideshowSliding = 'true';

    try {
      this.preloadSlideshowSlide(index);
      await this.waitForSlideImages(incomingSlide);

      const shouldUseForwardLoopClone =
        !this.slideshowLoopEnabled && prevIndex === count - 1 && index === 0 && direction === 1;
      const loopClone = shouldUseForwardLoopClone ? this.createSlideshowLoopClone(index) : null;
      const targetSlide = loopClone || incomingSlide;
      const targetLeft = targetSlide.offsetLeft;

      this.slider.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });
      await this.waitForSlideshowScroll(targetLeft);

      if (loopClone) {
        this.setSlideshowScrollPosition(index);
        loopClone.remove();
      }
    } finally {
      delete this.slideshow.dataset.gigiSlideshowSliding;
    }

    this.finishSlideshowNavigation(index);
  }

  preloadSlideshowSlide(index) {
    if (!this.slides.length) return;

    const nextSlide = this.slides[index % this.slides.length];
    if (!nextSlide) return;

    nextSlide.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  }

  getSlideCount() {
    if (this.mode === 'slideshow') {
      return this.slides.length || this.dots.length;
    }

    if (this.mode === 'card') {
      return this.slides.length;
    }

    return this.thumbs.length;
  }

  getSlideDirection(prevIndex, nextIndex, count) {
    if (prevIndex === nextIndex) return 0;
    if (nextIndex === prevIndex + 1) return 1;
    if (nextIndex === prevIndex - 1) return -1;
    if (prevIndex === count - 1 && nextIndex === 0) return 1;
    if (prevIndex === 0 && nextIndex === count - 1) return -1;
    return nextIndex > prevIndex ? 1 : -1;
  }

  getActiveFill() {
    return this.querySelector('[data-carousel-loading-bar-dot].is-active [data-carousel-loading-bar-fill]');
  }

  onHoverEnter(event) {
    if (this.getSlideCount() < 2) return;
    if (this.isPlaying) return;
    this.startHover();
  }

  startHover() {
    if (this.mode === 'slideshow' || this.reducedMotion?.matches) return;

    this.isPlaying = true;
    this.classList.add('is-playing');
    this.classList.remove('is-paused');
    this.gallery.classList.add('is-carousel-hovering');
    this.playProgress();
  }

  stopHover() {
    if (!this.isPlaying) return;

    this.isPlaying = false;
    this.classList.add('is-paused');
    this.gallery.classList.remove('is-carousel-hovering');

    const fill = this.getActiveFill();
    if (fill?.style.animation) {
      fill.style.animationPlayState = 'paused';
    }
  }

  onHoverLeave(event) {
    if (this.hoverTarget.contains(event.relatedTarget)) return;
    this.stopHover();
  }

  bindSlideshowSwipe() {
    if (this.mode !== 'slideshow' || this.slideshowSwipeBound || !window.PointerEvent) return;

    this.slideshowSwipeStage = this.slider;
    if (!this.slideshowSwipeStage) return;

    this.slideshowSwipeStage.addEventListener('pointerdown', this.onSlideshowPointerDown);
    this.slideshowSwipeStage.addEventListener('click', this.onSlideshowClickCapture, true);
    this.slideshowSwipeStage.addEventListener('dragstart', this.onSlideshowDragStart);
    this.slideshowSwipeBound = true;
  }

  unbindSlideshowSwipe() {
    if (!this.slideshowSwipeBound || !this.slideshowSwipeStage) return;

    this.slideshowSwipeStage.removeEventListener('pointerdown', this.onSlideshowPointerDown);
    this.slideshowSwipeStage.removeEventListener('click', this.onSlideshowClickCapture, true);
    this.slideshowSwipeStage.removeEventListener('dragstart', this.onSlideshowDragStart);
    this.clearSlideshowDragState();
    this.slideshowSwipeBound = false;
    this.slideshowSwipeStage = null;
  }

  isDesktopSlideshowSwipeEnabled() {
    const isDesktop = !window.matchMedia || window.matchMedia(CarouselLoadingBar.DESKTOP_QUERY).matches;
    return this.mode === 'slideshow' && isDesktop && this.getSlideCount() > 1;
  }

  isInteractiveSlideshowSwipeTarget(target) {
    return Boolean(
      target?.closest?.(
        'a, button, input, select, textarea, summary, video, iframe, .slideshow__text, .slideshow__loading-bar, carousel-loading-bar'
      )
    );
  }

  isSlideshowSwipeTarget(target) {
    if (!(target instanceof Element)) return false;
    if (!this.slideshow?.contains(target)) return false;
    if (this.isInteractiveSlideshowSwipeTarget(target)) return false;

    return target === this.slider || Boolean(target.closest('.slideshow__media, .slideshow__slide'));
  }

  getClosestSlideshowIndex() {
    if (!this.slider || !this.slides.length) return this.activeIndex;

    if (this.slideshowLoopEnabled) {
      const repr = this.getSlideshowReprIndex();
      if (repr != null) return repr;
    }

    return this.slides.reduce(
      (closest, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - this.slider.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      },
      { index: this.activeIndex, distance: Infinity }
    ).index;
  }

  getSlideshowDragTargetIndex(deltaX, deltaY) {
    const count = this.getSlideCount();
    if (count < 2) return this.activeIndex;

    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX >= CarouselLoadingBar.SWIPE_MIN_DISTANCE && absX > absY * CarouselLoadingBar.SWIPE_AXIS_RATIO) {
      const direction = deltaX < 0 ? 1 : -1;
      const startIndex = Number.isInteger(this.slideshowStartIndex) ? this.slideshowStartIndex : this.activeIndex;
      if (this.slideshowLoopEnabled) {
        return (startIndex + direction + count) % count;
      }
      return Math.max(0, Math.min(count - 1, startIndex + direction));
    }

    return this.getClosestSlideshowIndex();
  }

  snapSlideshowToIndex(index, restartProgress = true) {
    if (!this.slider || !this.slides[index]) return;

    const shouldRestartProgress = restartProgress && this.isPlaying;
    if (shouldRestartProgress) {
      this.clearProgressAnimation(false);
    }

    this.preloadSlideshowSlide(index + 1);

    // A drag that crosses an end wraps via the edge clone instead of rewinding.
    if (this.slideshowLoopEnabled) {
      const count = this.realSlides.length;
      const from = this.activeIndex;
      const isForwardWrap = from === count - 1 && index === 0;
      const isBackwardWrap = from === 0 && index === count - 1;
      if (isForwardWrap || isBackwardWrap) {
        this.runSlideshowLoopWrap(index, isForwardWrap ? 1 : -1, shouldRestartProgress);
        return;
      }
    }

    this.slider.scrollTo({
      left: this.slides[index].offsetLeft,
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    });
    this.setActiveIndex(index, shouldRestartProgress);
    if (this.slideshowLoopEnabled) this.applyLoopVisibility(index);
    this.dispatchSlideshowSlideChanged(index);
    if (shouldRestartProgress) this.slideshow?.play?.();
  }

  bindDocumentSlideshowSwipeEvents() {
    if (this.slideshowDocumentSwipeBound) return;

    this.slideshowDocumentSwipeBound = true;
    document.addEventListener('pointermove', this.onSlideshowPointerMove);
    document.addEventListener('pointerup', this.onSlideshowPointerUp);
    document.addEventListener('pointercancel', this.onSlideshowPointerCancel);
    window.addEventListener('blur', this.onSlideshowPointerCancel);
  }

  unbindDocumentSlideshowSwipeEvents() {
    if (!this.slideshowDocumentSwipeBound) return;

    this.slideshowDocumentSwipeBound = false;
    document.removeEventListener('pointermove', this.onSlideshowPointerMove);
    document.removeEventListener('pointerup', this.onSlideshowPointerUp);
    document.removeEventListener('pointercancel', this.onSlideshowPointerCancel);
    window.removeEventListener('blur', this.onSlideshowPointerCancel);
  }

  clearSlideshowDragState() {
    this.unbindDocumentSlideshowSwipeEvents();

    if (this.slider && typeof this.slideshowPreviousScrollBehavior === 'string') {
      this.slider.style.scrollBehavior = this.slideshowPreviousScrollBehavior;
    }

    this.slider?.classList.remove('is-gigi-dragging');
    if (this.slideshow) {
      delete this.slideshow.dataset.gigiSlideshowDragging;
    }

    this.slideshowPointerId = null;
    this.slideshowTracking = false;
    this.slideshowHorizontalSwipe = false;
    this.slideshowStartIndex = null;
    this.slideshowPreviousScrollBehavior = null;
  }

  onSlideshowPointerDown(event) {
    if (!this.isDesktopSlideshowSwipeEnabled()) return;
    if (event.pointerType === 'touch' || event.button > 0) return;
    if (!this.isSlideshowSwipeTarget(event.target)) return;
    if (
      this.slideshow.dataset.gigiSlideshowSliding === 'true' ||
      this.slideshow.dataset.gigiSlideshowDragging === 'true'
    ) {
      return;
    }

    this.primeSlideshowClones();

    this.slideshowStartX = event.clientX;
    this.slideshowStartY = event.clientY;
    this.slideshowStartScrollLeft = this.slider.scrollLeft;
    this.slideshowStartIndex = this.activeIndex;
    this.slideshowPointerId = event.pointerId;
    this.slideshowTracking = true;
    this.slideshowHorizontalSwipe = false;
    this.slideshowDidDrag = false;
    this.bindDocumentSlideshowSwipeEvents();

    if (this.slideshowSwipeStage.setPointerCapture) {
      try {
        this.slideshowSwipeStage.setPointerCapture(this.slideshowPointerId);
      } catch (error) {
        // Capture can fail when the browser has already claimed the gesture.
      }
    }
  }

  onSlideshowPointerMove(event) {
    if (!this.slideshowTracking || event.pointerId !== this.slideshowPointerId) return;

    const deltaX = event.clientX - this.slideshowStartX;
    const deltaY = event.clientY - this.slideshowStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (
      !this.slideshowHorizontalSwipe &&
      absX > CarouselLoadingBar.SWIPE_START_DISTANCE &&
      absX > absY * CarouselLoadingBar.SWIPE_AXIS_RATIO
    ) {
      this.slideshowHorizontalSwipe = true;
      this.slideshowPreviousScrollBehavior = this.slider.style.scrollBehavior;
      this.slider.style.scrollBehavior = 'auto';
      this.slider.classList.add('is-gigi-dragging');
      this.slideshow.dataset.gigiSlideshowDragging = 'true';

      if (this.isPlaying) {
        this.clearProgressAnimation(false);
      }
    }

    if (!this.slideshowHorizontalSwipe) return;

    event.preventDefault();
    this.slideshowDidDrag = true;
    this.slider.scrollLeft = this.slideshowStartScrollLeft - deltaX;
  }

  onSlideshowPointerUp(event) {
    if (!this.slideshowTracking || event.pointerId !== this.slideshowPointerId) return;

    const deltaX = event.clientX - this.slideshowStartX;
    const deltaY = event.clientY - this.slideshowStartY;
    const shouldSettle = this.slideshowHorizontalSwipe;
    const targetIndex = shouldSettle ? this.getSlideshowDragTargetIndex(deltaX, deltaY) : this.activeIndex;

    if (this.slideshowSwipeStage.releasePointerCapture) {
      try {
        this.slideshowSwipeStage.releasePointerCapture(this.slideshowPointerId);
      } catch (error) {
        // Capture may already be released after a browser-level gesture.
      }
    }

    if (shouldSettle) {
      event.preventDefault();
      this.suppressSlideshowClick = this.slideshowDidDrag;
      window.setTimeout(() => {
        this.suppressSlideshowClick = false;
      }, CarouselLoadingBar.SLIDE_MS + 120);
    }

    this.clearSlideshowDragState();

    if (shouldSettle) {
      this.snapSlideshowToIndex(targetIndex);
    }
  }

  onSlideshowPointerCancel() {
    const shouldSettle = this.slideshowHorizontalSwipe;
    const targetIndex = shouldSettle ? this.getClosestSlideshowIndex() : this.activeIndex;

    this.clearSlideshowDragState();

    if (shouldSettle) {
      this.snapSlideshowToIndex(targetIndex);
    }
  }

  onSlideshowClickCapture(event) {
    if (!this.suppressSlideshowClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.suppressSlideshowClick = false;
  }

  onSlideshowDragStart(event) {
    if (!this.isDesktopSlideshowSwipeEnabled()) return;
    if (!this.isSlideshowSwipeTarget(event.target)) return;

    event.preventDefault();
  }

  onGallerySlideChanged(event) {
    const index = event.detail?.index;
    if (typeof index !== 'number' || index < 0) return;

    this.setActiveIndex(index, this.isPlaying);
  }

  onGalleryUserNavigated() {
    if (this.mode !== 'panel') return;

    if (this.isMobileHomeProductGallery() || this.isPlaying) {
      this.restartAutoplay();
      return;
    }
  }

  restartAutoplay(index = this.activeIndex) {
    if (this.mode === 'slideshow' || this.reducedMotion?.matches || this.getSlideCount() < 2) return;

    this.isPlaying = true;
    this.classList.add('is-playing');
    this.classList.remove('is-paused');
    this.gallery?.classList.add('is-carousel-hovering');
    this.setActiveIndex(index, true);
  }

  syncFromGallery() {
    const activeIndex = this.thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true');
    this.setActiveIndex(activeIndex >= 0 ? activeIndex : 0, false);
  }

  syncFromSlides() {
    const activeIndex = parseInt(this.gallery.dataset.gigiActiveIndex || '0', 10);
    this.setActiveIndex(Number.isNaN(activeIndex) ? 0 : activeIndex, false);
  }

  goToSlide(index) {
    if (this.mode !== 'card' || this.slides.length < 2) return;
    if (this.gallery.dataset.gigiGallerySliding === 'true') return;

    const prevIndex = this.activeIndex;
    if (prevIndex === index) return;

    this.preloadSlide(index + 1);

    this.slides.forEach((slide, slideIndex) => {
      slide.classList.remove('is-sliding', 'is-outgoing', 'is-incoming');
      slide.classList.toggle('is-active', slideIndex === index);
      slide.style.transform = '';
      slide.style.opacity = '';
      slide.style.transition = '';
    });

    this.gallery.dataset.gigiActiveIndex = String(index);
    this.setActiveIndex(index, this.isPlaying);
  }

  bindCardSwipe() {
    if (this.mode !== 'card' || this.cardSwipeBound || !window.PointerEvent) return;

    this.cardSwipeStage =
      this.gallery.querySelector('.gigi-card-media-gallery__stage') ||
      this.gallery.querySelector('.gigi-card-media-gallery__hover') ||
      this.gallery;

    if (!this.cardSwipeStage) return;

    this.cardSwipeStage.addEventListener('pointerdown', this.onCardPointerDown);
    this.cardSwipeStage.addEventListener('click', this.onCardClickCapture, true);
    this.cardSwipeStage.addEventListener('dragstart', this.onCardDragStart);
    this.cardSwipeBound = true;
  }

  unbindCardSwipe() {
    if (!this.cardSwipeBound || !this.cardSwipeStage) return;

    this.cardSwipeStage.removeEventListener('pointerdown', this.onCardPointerDown);
    this.cardSwipeStage.removeEventListener('click', this.onCardClickCapture, true);
    this.cardSwipeStage.removeEventListener('dragstart', this.onCardDragStart);
    this.unbindDocumentCardSwipeEvents();
    this.clearCardDragState(this.cardDragState);
    this.resetCardSwipe();
    this.cardSwipeBound = false;
    this.cardSwipeStage = null;
  }

  isCardSwipeEnabled() {
    return this.mode === 'card' && this.slides.length > 1;
  }

  isInteractiveCardSwipeTarget(target) {
    if (!(target instanceof Element)) return false;
    if (target.closest('input, select, textarea, summary, [data-gigi-card-zoom]')) return true;

    const button = target.closest('button');
    return Boolean(button && !button.closest('carousel-loading-bar'));
  }

  getCardSwipeAxisRatio() {
    return this.cardPointerType === 'mouse'
      ? CarouselLoadingBar.SWIPE_AXIS_RATIO
      : CarouselLoadingBar.SWIPE_TOUCH_AXIS_RATIO;
  }

  getCardSwipeTarget(deltaX) {
    if (!this.isCardSwipeEnabled()) return null;

    const count = this.slides.length;
    const direction = deltaX < 0 ? 1 : -1;
    const activeIndex = Math.max(0, Math.min(this.activeIndex, count - 1));
    const index = (activeIndex + direction + count) % count;

    return {
      direction,
      index,
      incoming: this.slides[index],
      outgoing: this.slides[activeIndex],
    };
  }

  resetCardSlideStyles() {
    this.slides.forEach((slide) => {
      slide.classList.remove('is-sliding', 'is-outgoing', 'is-incoming');
      slide.style.transform = '';
      slide.style.opacity = '';
      slide.style.transition = '';
      slide.style.zIndex = '';
      slide.style.pointerEvents = '';
    });
  }

  clearCardDragState(state) {
    if (!state) return;

    this.resetCardSlideStyles();
    delete this.gallery.dataset.gigiGalleryDragging;
    if (!state.wasPlaying) {
      this.gallery.classList.remove('is-carousel-hovering');
    }
  }

  createCardDragState(deltaX) {
    if (!this.isCardSwipeEnabled() || this.gallery.dataset.gigiGallerySliding === 'true') return null;

    const target = this.getCardSwipeTarget(deltaX);
    const width = this.cardSwipeStage?.getBoundingClientRect().width || 0;
    if (!target?.incoming || !target.outgoing || !width) return null;

    this.preloadSlide(target.index);
    this.gallery.dataset.gigiGalleryDragging = 'true';
    this.gallery.classList.add('is-carousel-hovering');

    if (this.isPlaying) {
      this.clearProgressAnimation(false);
    }

    this.resetCardSlideStyles();

    target.outgoing.classList.add('is-sliding', 'is-outgoing');
    target.outgoing.style.opacity = '1';
    target.outgoing.style.pointerEvents = 'none';
    target.outgoing.style.transition = 'none';
    target.outgoing.style.transform = 'translateX(0)';
    target.outgoing.style.zIndex = '3';

    target.incoming.classList.add('is-sliding', 'is-incoming');
    target.incoming.style.opacity = '1';
    target.incoming.style.pointerEvents = 'none';
    target.incoming.style.transition = 'none';
    target.incoming.style.transform = `translateX(${target.direction * width}px)`;
    target.incoming.style.zIndex = '2';

    return {
      ...target,
      width,
      wasPlaying: this.isPlaying,
    };
  }

  setCardDragTransforms(state, deltaX) {
    const clampedDelta = Math.max(-state.width, Math.min(state.width, deltaX));

    state.outgoing.style.transform = `translateX(${clampedDelta}px)`;
    state.incoming.style.transform = `translateX(${state.direction * state.width + clampedDelta}px)`;
  }

  finishCardDrag(state, shouldCommit) {
    if (!state) return;

    state.outgoing.style.transition = `transform ${CarouselLoadingBar.SLIDE_MS}ms ease, opacity ${CarouselLoadingBar.SLIDE_MS}ms ease`;
    state.incoming.style.transition = `transform ${CarouselLoadingBar.SLIDE_MS}ms ease, opacity ${CarouselLoadingBar.SLIDE_MS}ms ease`;
    state.incoming.offsetHeight;

    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      this.cardFinishActiveDrag = null;

      if (shouldCommit) {
        this.setCardSlideInstant(state.index);
        this.setActiveIndex(state.index, this.isPlaying);
        this.restartAutoplay(state.index);
      } else {
        this.resetCardSlideStyles();
        if (state.wasPlaying && this.hoverTarget?.matches?.(':hover')) {
          this.restartAutoplay(this.activeIndex);
        } else if (state.wasPlaying) {
          this.clearProgressAnimation();
        } else {
          this.gallery.classList.remove('is-carousel-hovering');
        }
      }

      delete this.gallery.dataset.gigiGallerySliding;
      delete this.gallery.dataset.gigiGalleryDragging;
      this.cardDragState = null;
    };

    if (shouldCommit) {
      this.gallery.dataset.gigiGallerySliding = 'true';
      state.outgoing.style.transform = `translateX(${state.direction * -state.width}px)`;
      state.incoming.style.transform = 'translateX(0)';
      this.suppressNextCardClick();
    } else {
      state.outgoing.style.transform = 'translateX(0)';
      state.incoming.style.transform = `translateX(${state.direction * state.width}px)`;
    }

    // Lets the next pointerdown fast-forward this animation instead of being ignored.
    this.cardFinishActiveDrag = finish;
    state.incoming.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, CarouselLoadingBar.SLIDE_MS + 80);
  }

  resetCardSwipe() {
    this.unbindDocumentCardSwipeEvents();
    this.cardStartX = 0;
    this.cardStartY = 0;
    this.cardPointerId = null;
    this.cardPointerType = null;
    this.cardTracking = false;
    this.cardHorizontalSwipe = false;
    this.cardDidDrag = false;
    this.cardVelocityX = 0;
    this.cardLastMoveX = 0;
    this.cardLastMoveTime = 0;
    this.cardStartedOnLoadingBar = false;
  }

  onCardPointerDown(event) {
    if (!this.isCardSwipeEnabled()) return;
    if (event.button > 0) return;
    if (this.isInteractiveCardSwipeTarget(event.target)) return;
    if (this.gallery.dataset.gigiGallerySliding === 'true' || this.gallery.dataset.gigiGalleryDragging === 'true') {
      if (!this.cardFinishActiveDrag) return;
      this.cardFinishActiveDrag();
    }

    this.cardStartX = event.clientX;
    this.cardStartY = event.clientY;
    this.cardPointerId = event.pointerId;
    this.cardPointerType = event.pointerType;
    this.cardTracking = true;
    this.cardHorizontalSwipe = false;
    this.cardDidDrag = false;
    this.cardDragState = null;
    this.cardVelocityX = 0;
    this.cardLastMoveX = event.clientX;
    this.cardLastMoveTime = event.timeStamp;
    this.cardStartedOnLoadingBar = Boolean(
      event.target instanceof Element && event.target.closest('carousel-loading-bar')
    );
    this.bindDocumentCardSwipeEvents();

    // Explicit capture retargets the synthesized click to the stage, which would break dot taps.
    if (!this.cardStartedOnLoadingBar && this.cardSwipeStage.setPointerCapture) {
      try {
        this.cardSwipeStage.setPointerCapture(this.cardPointerId);
      } catch (error) {
        // Capture can fail when the browser has already claimed the gesture.
      }
    }
  }

  onCardPointerMove(event) {
    if (!this.cardTracking || event.pointerId !== this.cardPointerId) return;

    const deltaX = event.clientX - this.cardStartX;
    const deltaY = event.clientY - this.cardStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    const elapsedMs = event.timeStamp - this.cardLastMoveTime;
    if (elapsedMs > 0) {
      const velocityX = (event.clientX - this.cardLastMoveX) / elapsedMs;
      this.cardVelocityX = this.cardVelocityX * 0.2 + velocityX * 0.8;
      this.cardLastMoveX = event.clientX;
      this.cardLastMoveTime = event.timeStamp;
    }

    const target = absX > CarouselLoadingBar.SWIPE_START_DISTANCE ? this.getCardSwipeTarget(deltaX) : null;

    if (
      !this.cardHorizontalSwipe &&
      target &&
      absX > CarouselLoadingBar.SWIPE_START_DISTANCE &&
      absX > absY * this.getCardSwipeAxisRatio()
    ) {
      this.cardHorizontalSwipe = true;
    }

    if (!this.cardHorizontalSwipe) return;

    event.preventDefault();
    this.cardDidDrag = true;

    if (target && (!this.cardDragState || this.cardDragState.direction !== target.direction)) {
      this.clearCardDragState(this.cardDragState);
      this.cardDragState = this.createCardDragState(deltaX);
    }

    if (this.cardDragState) {
      this.setCardDragTransforms(this.cardDragState, deltaX);
    }
  }

  onCardPointerUp(event) {
    if (!this.cardTracking || event.pointerId !== this.cardPointerId) return;

    const deltaX = event.clientX - this.cardStartX;
    const deltaY = event.clientY - this.cardStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    this.releaseCardPointerCapture();

    if (this.cardDragState) {
      event.preventDefault();
      this.finishCardDrag(this.cardDragState, this.shouldCommitCardDrag(this.cardDragState, deltaX, event.timeStamp));
      if (this.cardDidDrag) {
        this.suppressNextCardClick();
      }
    } else if (absX >= CarouselLoadingBar.SWIPE_MIN_DISTANCE && absX > absY * this.getCardSwipeAxisRatio()) {
      event.preventDefault();

      const target = this.getCardSwipeTarget(deltaX);
      if (target) {
        this.goToSlide(target.index);
        this.restartAutoplay(target.index);
        this.suppressNextCardClick();
      }
    }

    this.resetCardSwipe();
  }

  shouldCommitCardDrag(state, deltaX, releaseTime) {
    const progress = deltaX * -state.direction;
    if (progress <= 0) return false;

    const commitDistance = Math.min(
      CarouselLoadingBar.SWIPE_MIN_DISTANCE,
      Math.max(CarouselLoadingBar.SWIPE_COMMIT_MIN_DISTANCE, state.width * CarouselLoadingBar.SWIPE_COMMIT_FRACTION)
    );
    if (progress >= commitDistance) return true;

    // A still finger produces no pointermove events, so the last sample can be stale-fast.
    const velocityIsStale = releaseTime - this.cardLastMoveTime > 100;
    const flickVelocity = velocityIsStale ? 0 : this.cardVelocityX * -state.direction;
    return (
      flickVelocity >= CarouselLoadingBar.SWIPE_FLICK_VELOCITY &&
      progress >= CarouselLoadingBar.SWIPE_FLICK_MIN_DISTANCE
    );
  }

  onCardPointerCancel() {
    this.releaseCardPointerCapture();

    if (this.cardDragState) {
      this.finishCardDrag(this.cardDragState, false);
    }

    this.resetCardSwipe();
  }

  bindDocumentCardSwipeEvents() {
    if (this.cardDocumentSwipeBound) return;

    this.cardDocumentSwipeBound = true;
    document.addEventListener('pointermove', this.onCardPointerMove);
    document.addEventListener('pointerup', this.onCardPointerUp);
    document.addEventListener('pointercancel', this.onCardPointerCancel);
    window.addEventListener('blur', this.onCardPointerCancel);
  }

  unbindDocumentCardSwipeEvents() {
    if (!this.cardDocumentSwipeBound) return;

    this.cardDocumentSwipeBound = false;
    document.removeEventListener('pointermove', this.onCardPointerMove);
    document.removeEventListener('pointerup', this.onCardPointerUp);
    document.removeEventListener('pointercancel', this.onCardPointerCancel);
    window.removeEventListener('blur', this.onCardPointerCancel);
  }

  releaseCardPointerCapture() {
    if (!this.cardSwipeStage?.releasePointerCapture || this.cardPointerId === null) return;

    try {
      this.cardSwipeStage.releasePointerCapture(this.cardPointerId);
    } catch (error) {
      // Capture may already be released after a browser-level gesture.
    }
  }

  suppressNextCardClick() {
    this.suppressCardClick = true;
    window.clearTimeout(this.cardClickSuppressTimer);
    this.cardClickSuppressTimer = window.setTimeout(() => {
      this.suppressCardClick = false;
    }, CarouselLoadingBar.SLIDE_MS + 120);
  }

  onCardClickCapture(event) {
    if (!this.suppressCardClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.suppressCardClick = false;
  }

  onCardDragStart(event) {
    if (!this.isCardSwipeEnabled()) return;
    if (this.isInteractiveCardSwipeTarget(event.target)) return;

    event.preventDefault();
  }

  setCardSlideInstant(index) {
    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === index);
      slide.style.transform = '';
      slide.style.opacity = '';
      slide.style.transition = '';
      slide.style.zIndex = '';
      slide.style.pointerEvents = '';
    });
    this.gallery.dataset.gigiActiveIndex = String(index);
  }

  preloadSlide(index) {
    if (!this.slides.length) return;

    const nextIndex = index % this.slides.length;
    const nextSlide = this.slides[nextIndex];

    if (nextSlide && nextSlide.loading === 'lazy') {
      nextSlide.loading = 'eager';
    }
  }

  setActiveIndex(index, restartProgress = false) {
    this.activeIndex = index;

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (restartProgress) {
      this.clearProgressAnimation(false);
      this.playProgress();
    }
  }

  playProgress() {
    if (this.reducedMotion?.matches || !this.isPlaying || this.getSlideCount() < 2) return;

    const fill = this.getActiveFill();
    if (!fill) return;

    if (fill.style.animation && fill.style.animationPlayState === 'paused') {
      this.classList.add('is-autoplaying');
      this.classList.remove('is-paused');
      fill.style.animationPlayState = 'running';
      return;
    }

    this.clearProgressAnimation(false);
    this.classList.add('is-autoplaying');
    this.classList.remove('is-paused');

    fill.addEventListener('animationend', this.onProgressEnd, { once: true });
    const slideshowSpeed = Number.parseFloat(this.slider?.dataset.speed || '');
    const duration =
      this.mode === 'slideshow' && Number.isFinite(slideshowSpeed) && slideshowSpeed > 0
        ? slideshowSpeed * 1000
        : this.isMobileHomeProductGallery()
          ? CarouselLoadingBar.MOBILE_HOME_PRODUCT_DURATION_MS
          : CarouselLoadingBar.DURATION_MS;

    fill.style.animation = `carousel-loading-bar-progress ${duration}ms linear forwards`;
    fill.style.animationPlayState = 'running';
  }

  onProgressEnd() {
    if (!this.isPlaying) return;

    if (this.mode === 'slideshow') return;

    const count = this.getSlideCount();
    const nextIndex = (this.activeIndex + 1) % count;

    if (this.mode === 'panel') {
      this.thumbs[nextIndex]?.click();
      return;
    }

    this.goToSlide(nextIndex);
  }

  clearProgressAnimation(resetPlayingState = true) {
    this.querySelectorAll('[data-carousel-loading-bar-fill]').forEach((fill) => {
      fill.removeEventListener('animationend', this.onProgressEnd);
      fill.style.animation = 'none';
      fill.style.animationPlayState = '';
    });

    this.classList.remove('is-autoplaying');

    if (resetPlayingState) {
      this.isPlaying = false;
      this.classList.remove('is-playing', 'is-paused');
      this.gallery?.classList.remove('is-carousel-hovering');
    }
  }
}

customElements.define('carousel-loading-bar', CarouselLoadingBar);
})();
